/**
 * Shared feedback UI primitives — Spinner, ErrorBanner, SuccessBanner, Modal.
 *
 * Previously duplicated as:
 *   AdminSection  → Spinner, ErrorBanner, SuccessBanner, Modal
 *   ConfigSection → CfgSpinner, CfgError, CfgSuccess
 *
 * CSS class names use the "fb-" prefix. Import a companion CSS file or
 * rely on inline styles via the `inline` prop.
 */

import React from 'react';

export function Spinner({ message = 'Loading…', className }) {
    return (
        <div className={className || 'fb-spinner'}
             style={{ padding: 20, color: '#888', textAlign: 'center' }}>
            ⏳ {message}
        </div>
    );
}

export function ErrorBanner({ msg, onDismiss, className }) {
    if (!msg) return null;
    return (
        <div className={className || 'fb-error-banner'} style={{
            background: 'rgba(220, 38, 38, 0.12)',
            border: '1px solid rgba(220, 38, 38, 0.35)',
            borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, fontSize: 13,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12,
            color: 'var(--fg-text, #fecaca)',
        }}>
            <span style={{ color: 'var(--fg-text, #fecaca)', lineHeight: 1.4 }}>⚠️ {msg}</span>
            {onDismiss && (
                <button type="button" onClick={onDismiss}
                    style={{
                        border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16,
                        color: 'var(--fg-text-3, #fca5a5)', lineHeight: 1,
                    }}>✕</button>
            )}
        </div>
    );
}

export function SuccessBanner({ msg, onDismiss, className }) {
    if (!msg) return null;
    return (
        <div className={className || 'fb-success-banner'} style={{
            background: 'rgba(34, 197, 94, 0.12)',
            border: '1px solid rgba(34, 197, 94, 0.35)',
            borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, fontSize: 13,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12,
        }}>
            <span style={{ color: 'var(--fg-text, #bbf7d0)', lineHeight: 1.4 }}>✅ {msg}</span>
            {onDismiss && (
                <button type="button" onClick={onDismiss}
                    style={{
                        border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16,
                        color: 'var(--fg-text-3, #86efac)', lineHeight: 1,
                    }}>✕</button>
            )}
        </div>
    );
}

export function InfoBanner({ message, title, dismissible = false, onDismiss, action, className }) {
    if (!message) return null;
    return (
        <div
            role="alert"
            aria-label={title ? `Info: ${title}` : 'Info'}
            className={className || 'fb-info-banner'}
            style={{
                background: '#e3f2fd',
                border: '1px solid rgba(21, 101, 192, 0.35)',
                borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, fontSize: 13,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12,
                color: '#1565c0',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                {title && (
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#1565c0' }}>{title}</span>
                )}
                <span style={{ color: '#1565c0', lineHeight: 1.4 }}>ℹ️ {message}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        style={{
                            border: '1px solid #1565c0', background: 'transparent', cursor: 'pointer',
                            fontSize: 12, color: '#1565c0', padding: '4px 10px',
                            borderRadius: 6, fontWeight: 500, lineHeight: 1.4,
                        }}
                    >
                        {action.label}
                    </button>
                )}
                {dismissible && onDismiss && (
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={onDismiss}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 16, color: '#1565c0', lineHeight: 1,
                        }}
                    >✕</button>
                )}
            </div>
        </div>
    );
}

export function WarningBanner({ message, title, dismissible = false, onDismiss, action, className }) {
    if (!message) return null;
    return (
        <div
            role="alert"
            aria-label={title ? `Warning: ${title}` : 'Warning'}
            className={className || 'fb-warning-banner'}
            style={{
                background: '#fff3e0',
                border: '1px solid rgba(230, 81, 0, 0.35)',
                borderRadius: 8,
                padding: '10px 14px', marginBottom: 12, fontSize: 13,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 12,
                color: '#e65100',
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                {title && (
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#e65100' }}>{title}</span>
                )}
                <span style={{ color: '#e65100', lineHeight: 1.4 }}>⚠️ {message}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {action && (
                    <button
                        type="button"
                        onClick={action.onClick}
                        style={{
                            border: '1px solid #e65100', background: 'transparent', cursor: 'pointer',
                            fontSize: 12, color: '#e65100', padding: '4px 10px',
                            borderRadius: 6, fontWeight: 500, lineHeight: 1.4,
                        }}
                    >
                        {action.label}
                    </button>
                )}
                {dismissible && onDismiss && (
                    <button
                        type="button"
                        aria-label="Dismiss"
                        onClick={onDismiss}
                        style={{
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 16, color: '#e65100', lineHeight: 1,
                        }}
                    >✕</button>
                )}
            </div>
        </div>
    );
}

const KEY_INSIGHT_VARIANTS = {
    insight: { border: '#7b1fa2', bg: '#f3e5f5', text: '#4a148c' },
    tip:     { border: '#00695c', bg: '#e0f2f1', text: '#004d40' },
    important: { border: '#c62828', bg: '#ffebee', text: '#b71c1c' },
    example: { border: '#1565c0', bg: '#e3f2fd', text: '#0d47a1' },
};

export function KeyInsightCallout({ children, title = 'Key Insight', icon = '💡', variant = 'insight', className }) {
    const colors = KEY_INSIGHT_VARIANTS[variant] || KEY_INSIGHT_VARIANTS.insight;
    return (
        <div
            className={className || `fb-key-insight-callout fb-key-insight-callout--${variant}`}
            style={{
                borderLeft: `4px solid ${colors.border}`,
                background: colors.bg,
                borderRadius: '0 8px 8px 0',
                padding: '12px 16px',
                marginBottom: 12,
                color: colors.text,
            }}
        >
            {title && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontWeight: 700, fontSize: 13, marginBottom: 6,
                    color: colors.border,
                }}>
                    <span aria-hidden="true">{icon}</span>
                    <span>{title}</span>
                </div>
            )}
            <div style={{ fontSize: 13, lineHeight: 1.6, color: colors.text }}>
                {children}
            </div>
        </div>
    );
}

export function Modal({ title, onClose, children, className }) {
    return (
        <div className={className || 'fb-modal-overlay'}
             style={{
                 position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
                 display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
             }}
             onClick={onClose}>
            <div style={{
                     background: 'var(--fg-bg-2, #0D0C0B)',
                     border: '1px solid var(--fg-border, rgba(249,115,22,0.15))',
                     borderRadius: 12,
                     boxShadow: '0 16px 48px rgba(0,0,0,0.85), 0 0 30px rgba(249,115,22,0.05)',
                     width: 520, maxHeight: '90vh', overflowY: 'auto', padding: 24,
                 }}
                 onClick={e => e.stopPropagation()}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 16,
                }}>
                    <h3 style={{ margin: 0, color: 'var(--fg-text, #F5F0EB)' }}>{title}</h3>
                    <button onClick={onClose}
                        style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--fg-text-3, #6B6560)' }}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
}
